// ============================================================================
// STENZIA DISPLAYS — PRODUCT CATALOG (scalable architecture)
// ----------------------------------------------------------------------------
// All product data lives here. The website pages, routes, cards and SEO are
// derived from CATEGORIES + CATALOG. To add a new product:
//   1. Add an entry to CATALOG with a unique `slug` and the desired `category`.
//   2. Mark `status: "active"` and (optionally) `featured: true`.
// The Products page, individual /products/:slug route and the home strip will
// automatically pick it up — no layout or component changes required.
// ============================================================================

import { IMAGES } from "./images";

// ---------- CATEGORIES ----------
export const CATEGORIES = [
  {
    slug: "slider-display-systems",
    name: "STENZIA Glide Series",
    short: "Premium sliding tile display systems engineered for showroom-grade panel travel.",
    description:
      "Heavy-duty industrial slider systems manufactured from premium NIZONE CRC pipe with SS 202 sliding hardware and nylon guide wheels. Engineered for continuous showroom usage and architect-grade installations.",
    image: IMAGES.GlideHero,
    capacity: "Up to 10 ft × 6 ft panels",
    tag: "Bestseller",
    order: 1,
  },
  {
    slug: "booklet-display-systems",
    name: "STENZIA Flip Series",
    short:
      "Premium booklet tile display systems engineered for organized presentation and long-term durability.",
    description:
      "Premium booklet tile display systems engineered for organized tile presentation, smooth panel rotation and long-term showroom durability. Heavy-duty NIZONE CRC steel construction with precision bush rotation and rubber buffer protection — built for premium ceramic showrooms.",
    image: IMAGES.blackFramework,
    capacity: "24 – 40 panels",
    tag: "Professional Series",
    order: 2,
  },
  {
    slug: "motion-display-systems",
    name: "STENZIA Motion Series",
    short: "Advanced display systems engineered for flexibility, weight efficiency and compact footprints.",
    description:
      "Advanced tile display systems engineered around the same premium STENZIA sliding mechanism — extended with flexible widths, plywood-free construction and push-and-pull access. Designed for showrooms that demand maximum versatility per square metre.",
    image: IMAGES.architecture,
    capacity: "Up to 45 panels",
    tag: "Advanced",
    order: 3,
  },
  {
    slug: "rotating-display-systems",
    name: "STENZIA 360 Series",
    short: "Premium rotating tile display systems for compact showroom layouts.",
    description:
      "Premium rotating tile display systems engineered for compact showroom layouts, smooth 360-degree movement and maximum product presentation. Designed and manufactured by STENZIA Displays.",
    image: IMAGES.heroAlt,
    capacity: "Dual-side · up to 10 samples",
    tag: "New",
    order: 4,
  },
  {
    slug: "mockup-display-systems",
    name: "STENZIA Mockup Series",
    short: "Premium mockup display systems for realistic ceramic tile presentation.",
    description:
      "Premium mockup display systems designed for realistic ceramic tile presentation inside showrooms, experience centres and architectural display spaces. Heavy-duty NIZONE CRC structure, premium plywood and grey grooved carpet — engineered by STENZIA Displays.",
    image: IMAGES.heroShowroom,
    capacity: "4 ft – 12 ft (custom)",
    tag: "New",
    order: 5,
  },
  {
    slug: "display-accessories",
    name: "STENZIA Display Accessories",
    short: "Engineered hardware and accessories for tile display systems.",
    description:
      "Heavy-duty hardware and accessories engineered to support STENZIA display systems and the broader ceramic showroom industry. Premium materials, precision manufacturing and consistent finish.",
    image: IMAGES.steelRack,
    capacity: "Bulk / OEM / Retail",
    tag: "Accessories",
    order: 6,
    cards: [
      {
        slug: "minimal-tile-display",
        name: "Minimal Tile Display",
        body: "Slim-line CRC display stand engineered for minimal visual footprint while presenting tiles at architect-grade angle.",
      },
      {
        slug: "tile-holding-clamp",
        name: "Tile Holding Clamp",
        body: "Heavy-duty steel clamp engineered to grip ceramic tiles up to 800×1600 mm without surface marking.",
      },
      {
        slug: "italian-clamp",
        name: "Italian Clamp",
        body: "Premium Italian-style display clamp with anodised finish — engineered for flagship showroom installations.",
      },
      {
        slug: "nylon-guide-wheel",
        name: "Nylon Guide Wheel",
        body: "Premium nylon top guide wheel — silent, low-friction operation engineered for STENZIA Glide and Motion series.",
      },
      {
        slug: "ss202-panel-wheel",
        name: "SS202 Panel Wheel",
        body: "Stainless-steel SS 202 panel wheel — sealed bearing, friction-free travel, engineered for long-term showroom usage.",
      },
      {
        slug: "rubber-buffers",
        name: "Rubber Buffers",
        body: "Heavy-duty rubber buffer engineered to sit between display panels and prevent direct tile-on-tile contact.",
      },
    ],
  },
];

// ---------- PRODUCT CATALOG ----------
// Default specifications applied when a product doesn't override them.
const DEFAULT_SPECS = {
  Material: "Cold-rolled steel, 1.5–2.5 mm",
  Finish: "Electrostatic powder coating (any RAL colour)",
  Pre_treatment: "7-tank phosphate + epoxy primer",
  Lead_time: "10–15 working days (standard) · 25–35 (custom)",
};

const DEFAULT_FEATURES = [
  { icon: "Shield", title: "Heavy Duty Frame", body: "1.5–2.5 mm cold-rolled steel with reinforced welded joints." },
  { icon: "MoveHorizontal", title: "German Nylon Rollers", body: "Silent, friction-free movement rated for 100,000+ cycles." },
  { icon: "Droplets", title: "Rust Resistant", body: "Zinc-phosphate primer + epoxy topcoat for humid environments." },
  { icon: "Sparkles", title: "Premium Powder Coating", body: "Electrostatic 7-tank pre-treatment — 10+ year UV resistance." },
  { icon: "Ship", title: "Export Ready", body: "Container-optimised knock-down design for global shipping." },
  { icon: "Settings2", title: "Custom Branding", body: "Logo engraving, RAL colour matching and bespoke sizing." },
];

const make = (p) => ({
  status: "active",
  featured: false,
  best_seller: false,
  new_arrival: false,
  images: p.images && p.images.length ? p.images : [p.thumbnail],
  video: p.video || null,
  key_features: p.key_features || DEFAULT_FEATURES,
  features: p.features || [
    "Heavy-duty steel frame",
    "Premium electrostatic powder coating",
    "27-point QC inspection",
    "Custom sizing & branding",
    "Container-ready knock-down design",
  ],
  specifications: { ...DEFAULT_SPECS, ...(p.specifications || {}) },
  drawings: p.drawings || [],
  downloads: p.downloads || [
    { label: "Product Brochure", type: "PDF", url: "#" },
    { label: "Technical Catalogue", type: "PDF", url: "#" },
    { label: "Installation Guide", type: "PDF", url: "#" },
  ],
  tile_sizes: p.tile_sizes || ["300×600", "600×600", "600×1200", "800×1600"],
  applications: p.applications || ["Tile showrooms", "Ceramic retail", "Architectural specification"],
  seo_title: p.seo_title || `${p.name} — Stenzia Displays`,
  seo_description: p.seo_description || p.short,
  meta_keywords: p.meta_keywords || `${p.name}, tile display stand, ${p.category}, Stenzia Displays`,
  ...p,
});

// ---- SHARED CONSTRUCTION (Slider Display Systems) ----
const SLIDER_PANEL_SIZES = [
  "6 ft × 4 ft", "8 ft × 4 ft", "8 ft × 64\"", "8 ft × 6 ft",
  "10 ft × 4 ft", "10 ft × 64\"", "10 ft × 6 ft",
];

const SLIDER_APPLICATIONS = [
  "Tile showrooms",
  "Ceramic display centres",
  "Building material stores",
  "Architect studios",
  "Export display systems",
  "Retail experience centres",
];

const SLIDER_KEY_FEATURES = [
  { icon: "Shield", title: "Heavy Duty CRC Construction", body: "Premium NIZONE CRC pipe with reinforced welded joints — engineered for continuous showroom usage." },
  { icon: "MoveHorizontal", title: "SS 202 Sliding Rail", body: "Stainless steel SS 202 bottom rail and panel wheels for silent, friction-free panel travel." },
  { icon: "Cpu", title: "Premium Nylon Guide Wheels", body: "Top nylon guide wheels deliver smooth, low-maintenance operation over the service life." },
  { icon: "Sparkles", title: "Powder Coated Finish", body: "Electrostatic powder coating in any RAL colour — scratch, UV and humidity resistant." },
  { icon: "Award", title: "Architect-Grade Engineering", body: "CAD-driven design, FEA-verified load capacity and tight dimensional tolerances." },
  { icon: "Ship", title: "Export Quality Manufacturing", body: "Container-optimised knock-down construction. Made in India for global showrooms." },
];

const SLIDER_BASE_SPECS = {
  Panel_Frame: "2\" × 1\" NIZONE CRC pipe",
  Outer_Main_Structure: "3\" × 1.5\" NIZONE CRC pipe",
  Bottom_Sliding_Rail: "SS 202 sliding rail",
  Bottom_Panel_Wheels: "SS 202 panel wheels",
  Top_Guide_Wheels: "Premium nylon guide wheels",
  Finish: "Premium electrostatic powder coating",
  Construction: "Heavy duty industrial — CAD designed, jig-welded, 27-point QC",
  Origin: "Made in India",
  Lead_time: "10–15 working days (standard) · 25–35 (custom sizes)",
};

const SLIDER_CONFIGURATIONS = ["20 Panels", "24 Panels", "30 Panels", "36 Panels", "45 Panels"];
const SLIDER_SERIES = "STENZIA Glide Series";
const SLIDER_COLOURS = ["Matte Black", "Pure White", "Industrial Grey"];

const SLIDER_HIGHLIGHTS = [
  { icon: "Shield", title: "CRC Steel", body: "NIZONE cold-rolled steel pipe — 2\"×1\" frame and 3\"×1.5\" outer." },
  { icon: "MoveHorizontal", title: "SS202 Rail", body: "Stainless-steel SS 202 bottom rail + panel wheels for silent travel." },
  { icon: "Cpu", title: "Premium Nylon Wheels", body: "Top nylon guide wheels — friction-free, long service life." },
  { icon: "Sparkles", title: "Powder Coated Finish", body: "Electrostatic powder coat — scratch, UV and humidity resistant." },
];

const SLIDER_STANDARD_FEATURES = [
  "Heavy-Duty CRC Construction",
  "Premium Powder Coating",
  "Smooth Sliding",
  "Low Maintenance",
  "Silent Operation",
  "Custom Branding",
  "Made in India",
];

export const CATALOG = [
  // ---- SLIDER DISPLAY SYSTEMS ----
  make({
    slug: "one-side-slope-slider",
    name: "Glide S",
    technical_name: "One Side Slope Tile Slider Display System",
    series: SLIDER_SERIES,
    model_designation: "Slope",
    category: "slider-display-systems",
    short: "Single-side inclined slider engineered for unobstructed eye-line viewing.",
    overview:
      "Glide S presents tiles on a single inclined face so each panel sits naturally at the customer's eye line. The slope geometry removes glare and parallax, giving showrooms a clearer, faster way to evaluate tile pattern, finish and grout interaction.",
    engineering_description:
      "Constructed from 2\"×1\" NIZONE CRC panel frames and a 3\"×1.5\" CRC outer structure, Glide S runs on SS 202 bottom rails with SS 202 panel wheels and premium nylon top guide wheels. Each panel is backed by 12 mm premium plywood and finished in electrostatic powder coat. The system is CAD-designed, jig-welded and passes a 27-point QC before despatch.",
    construction:
      "Welded steel chassis on adjustable feet, single-face slope geometry, knock-down design for container shipping. All hardware is mechanically fastened — no field welding required during installation.",
    description:
      "Glide S presents tiles on a single inclined face for clear eye-line viewing. NIZONE CRC pipe construction, SS 202 sliding hardware, nylon top guides and 12 mm ply panels — built for continuous showroom usage.",
   thumbnail: IMAGES.Glides,

hero: IMAGES.GlideHero,

images: [
  IMAGES.Glides,
],
    panel_capacity: "Single-side · up to 10 ft × 6 ft panels",
    material: "NIZONE CRC pipe — 2\"×1\" frame, 3\"×1.5\" outer",
    finish: "Premium powder coating (any RAL colour)",
    tile_sizes: SLIDER_PANEL_SIZES,
    configurations: SLIDER_CONFIGURATIONS,
    colours: SLIDER_COLOURS,
    specifications: { ...SLIDER_BASE_SPECS, Ply: "12 mm premium plywood (one face)", Orientation: "Inclined / slope" },
    standard_features: SLIDER_STANDARD_FEATURES,
    highlights: SLIDER_HIGHLIGHTS,
    key_features: SLIDER_KEY_FEATURES,
    applications: SLIDER_APPLICATIONS,
    features: [
      "2\" × 1\" NIZONE CRC panel frame",
      "3\" × 1.5\" NIZONE CRC outer structure",
      "SS 202 bottom sliding rail + SS 202 panel wheels",
      "Premium nylon top guide wheels — silent operation",
      "12 mm premium plywood panel backing",
      "Custom panel sizes (6 ft to 10 ft)",
    ],
    seo_title: "Glide S — One Side Slope Tile Slider | Stenzia Displays",
    seo_description: "Glide S inclined slider — NIZONE CRC pipe, SS 202 rail and 12 mm ply panels. Engineered for tile showrooms, architects and export.",
    og_title: "STENZIA Glide S — One Side Slope Slider",
    og_description: "Single-side inclined tile slider engineered with NIZONE CRC pipe, SS 202 sliding hardware and premium nylon guides.",
    meta_keywords: "Glide S, slope slider, tile slider rack, NIZONE CRC, SS 202 rail, Stenzia Displays",
    featured: true,
    best_seller: true,
  }),
  make({
    slug: "one-side-vertical-slider",
    name: "Glide Vertical",
    technical_name: "One Side Vertical Tile Slider Display System",
    series: SLIDER_SERIES,
    model_designation: "Vertical",
    category: "slider-display-systems",
    short: "Single-side vertical slider maximising display density per square metre.",
    overview:
      "Glide Vertical holds every panel in a true vertical plane, maximising tile-per-square-metre on a single display face. The clean upright geometry suits flagship showrooms where wall-aligned merchandising is required.",
    engineering_description:
      "The chassis uses 2\"×1\" and 3\"×1.5\" NIZONE CRC pipe with SS 202 bottom sliding rails, SS 202 panel wheels and premium nylon top guide wheels. Panels are backed by 12 mm premium plywood. Adjustable feet level the structure against uneven flooring.",
    construction:
      "Welded CRC frame with reinforced corner gussets, vertical panel guides, container-ready knock-down assembly. Finished in 7-tank pre-treated electrostatic powder coat.",
    description:
      "Glide Vertical holds every panel vertically for high-density showroom layouts. NIZONE CRC construction, SS 202 sliding hardware, nylon top guides and 12 mm ply.",
    thumbnail: IMAGES.GlideVertical,

hero: IMAGES.GlideHero,

images: [
  IMAGES.GlideVertical,
],
    panel_capacity: "Single-side · up to 10 ft × 6 ft panels",
    material: "NIZONE CRC pipe — 2\"×1\" frame, 3\"×1.5\" outer",
    finish: "Premium powder coating (any RAL colour)",
    tile_sizes: SLIDER_PANEL_SIZES,
    configurations: SLIDER_CONFIGURATIONS,
    colours: SLIDER_COLOURS,
    specifications: { ...SLIDER_BASE_SPECS, Ply: "12 mm premium plywood (one face)", Orientation: "Vertical" },
    standard_features: SLIDER_STANDARD_FEATURES,
    highlights: SLIDER_HIGHLIGHTS,
    key_features: SLIDER_KEY_FEATURES,
    applications: SLIDER_APPLICATIONS,
    features: [
      "2\" × 1\" NIZONE CRC panel frame",
      "3\" × 1.5\" NIZONE CRC outer structure",
      "SS 202 bottom sliding rail + SS 202 panel wheels",
      "Premium nylon top guide wheels — long service life",
      "12 mm premium plywood panel backing",
      "Architect-grade vertical alignment",
    ],
    seo_title: "Glide Vertical — One Side Vertical Tile Slider | Stenzia Displays",
    seo_description: "Glide Vertical vertical tile slider — NIZONE CRC pipe, SS 202 sliding hardware and 12 mm ply panels. Engineered for premium showrooms.",
    og_title: "STENZIA Glide Vertical — One Side Vertical Slider",
    og_description: "Single-side vertical tile slider with NIZONE CRC pipe construction, SS 202 rail and premium nylon top guides.",
    meta_keywords: "Glide Vertical, vertical slider, tile slider rack, NIZONE CRC, SS 202, one side slider",
    featured: true,
  }),
  make({
    slug: "both-side-slider",
    name: "Glide Duo",
    technical_name: "Both Side Tile Slider Display System",
    series: SLIDER_SERIES,
    model_designation: "Both Side",
    category: "slider-display-systems",
    short: "Dual-face slider engineered to double showroom display density.",
    overview:
      "Glide Duo presents tiles on two independent faces, doubling effective merchandising area without doubling floor footprint — ideal for free-standing showroom islands and high-traffic retail layouts.",
    engineering_description:
      "A reinforced 3\"×1.5\" NIZONE CRC central column carries the full dual-face load. Each face runs on its own SS 202 rail with SS 202 panel wheels and premium nylon top guide wheels. Panels are backed with 18 mm premium plywood — specified for the higher dual-face structural demand.",
    construction:
      "Free-standing reinforced CRC chassis, dual sliding guides, anti-tip leveling feet. Engineered for continuous customer interaction from both sides.",
    description:
      "Glide Duo runs panels on two independent faces from one reinforced CRC chassis. SS 202 sliding hardware on both sides, nylon guides, 18 mm ply panels.",
    thumbnail: IMAGES.GlideDuo,

hero: IMAGES.GlideHero,

images: [
  IMAGES.GlideDuo,
],
    panel_capacity: "Dual-side · up to 10 ft × 6 ft panels per face",
    material: "NIZONE CRC pipe — 2\"×1\" frame, 3\"×1.5\" outer (reinforced)",
    finish: "Premium powder coating (any RAL colour)",
    tile_sizes: SLIDER_PANEL_SIZES,
    configurations: SLIDER_CONFIGURATIONS,
    colours: SLIDER_COLOURS,
    specifications: { ...SLIDER_BASE_SPECS, Ply: "18 mm premium plywood (both faces)", Orientation: "Dual-face vertical" },
    standard_features: SLIDER_STANDARD_FEATURES,
    highlights: SLIDER_HIGHLIGHTS,
    key_features: SLIDER_KEY_FEATURES,
    applications: SLIDER_APPLICATIONS,
    features: [
      "2\" × 1\" NIZONE CRC panel frame",
      "3\" × 1.5\" NIZONE CRC reinforced outer structure",
      "SS 202 sliding rail + SS 202 wheels on both faces",
      "Premium nylon top guide wheels per face",
      "18 mm premium plywood (heavier dual-face load)",
      "Free-standing — no wall required",
    ],
    seo_title: "Glide Duo — Both Side Tile Slider | Stenzia Displays",
    seo_description: "Glide Duo dual-face slider — reinforced NIZONE CRC structure, SS 202 hardware and 18 mm ply panels. Doubles showroom density.",
    og_title: "STENZIA Glide Duo — Both Side Slider",
    og_description: "Dual-face tile slider engineered to double showroom display density with reinforced CRC chassis and SS 202 hardware on both sides.",
    meta_keywords: "Glide Duo, both side slider, double side slider, dual face tile rack, NIZONE CRC, SS 202",
    featured: true,
  }),

  // ============================================================================
  // BOOKLET DISPLAY SYSTEMS — STENZIA Flip Series
  // ============================================================================
  ...(() => {
    const SERIES = "STENZIA Flip Series";
    const APPLICATIONS = [
      "Ceramic Showrooms",
      "Tile Dealers",
      "Building Material Stores",
      "Architect Studios",
      "Interior Designers",
      "Export Display Centres",
      "Premium Retail Display",
    ];
    const COMMON_FEATURES = [
      "Heavy Duty NIZONE CRC Construction",
      "18 mm Premium Plywood",
      "Precision Bush Rotation",
      "Heavy Duty Rubber Buffer",
      "Smooth Manual Panel Rotation",
      "Silent Operation",
      "Premium Powder Coating",
      "Low Maintenance",
      "Modular Construction",
      "Custom Branding Available",
      "Made in India",
    ];
    const HIGHLIGHTS = [
      { icon: "Shield", title: "CRC Construction", body: "Heavy-duty NIZONE CRC steel — 2\"×1\" panel frame, 3\"×1.5\" or 4\"×4\" main structure." },
      { icon: "MoveHorizontal", title: "Bush Rotation", body: "Precision top and bottom bush assemblies deliver smooth, silent panel rotation." },
      { icon: "Droplets", title: "Rubber Buffer", body: "Heavy-duty rubber buffer between every panel prevents direct tile contact." },
      { icon: "Sparkles", title: "Powder Coated", body: "Premium electrostatic powder coating — scratch, UV and humidity resistant." },
    ];
    const KEY_FEATURES = [
      { icon: "Shield", title: "Heavy Duty CRC Construction", body: "NIZONE CRC steel — 2\"×1\" panel frame with 3\"×1.5\" or 4\"×4\" main structure, sized to the panel configuration." },
      { icon: "MoveHorizontal", title: "Precision Bush Rotation", body: "Top and bottom bush assemblies deliver smooth, silent, low-friction panel rotation over the full service life." },
      { icon: "Droplets", title: "Rubber Buffer Protection", body: "Heavy-duty rubber buffer between every panel eliminates tile-on-tile contact and abrasion damage." },
      { icon: "Cpu", title: "18 mm Premium Plywood", body: "Premium-grade 18 mm plywood backing engineered to carry full tile load without warping." },
      { icon: "Sparkles", title: "Premium Powder Coating", body: "Premium electrostatic powder coating in matte black, pure white or industrial grey — scratch, UV and humidity resistant." },
      { icon: "Ship", title: "Made in India", body: "Designed and manufactured by STENZIA Displays — built for global ceramic showrooms." },
    ];
    const BASE_SPECS = {
      Panel_Frame: "2\" × 1\" NIZONE CRC steel pipe",
      Main_Structure: "3\" × 1.5\" or 4\" × 4\" NIZONE CRC steel (per panel configuration)",
      Bush_Rotation: "Precision top & bottom bush rotation",
      Rubber_Buffer: "Heavy-duty rubber buffer between every panel",
      Plywood: "18 mm premium grade plywood",
      Finish: "Premium electrostatic powder coating",
      Colours: "Matte Black · Pure White · Industrial Grey",
      Available_Sizes: "6'×4', 8'×4', 10'×4' (custom on request)",
      Configurations: "24 / 36 / 40 panels (max 40)",
      Origin: "Designed & manufactured by STENZIA Displays · Made in India",
    };
    const TILE_SIZES = ["6' × 4'", "8' × 4'", "10' × 4'", "Custom"];
    const CONFIGS = ["24 Panels", "36 Panels", "40 Panels"];
    const COLOURS = ["Matte Black", "Pure White", "Industrial Grey"];

    return [
      make({
        slug: "flip-page",
        name: "STENZIA Flip Page",
        technical_name: "Page Style Booklet Tile Display System",
        series: SERIES,
        model_designation: "Page Style",
        category: "booklet-display-systems",
        short: "Sequential Browsing. Maximum Space Efficiency.",
        overview:
          "The Flip Page system arranges every display panel in a single continuous sequence, allowing customers to browse tiles exactly like turning the pages of a book.",
        engineering_description:
          "Each panel rotates independently using precision top and bottom bush assemblies, creating a smooth page-by-page browsing experience.",
        construction:
          "Available in both floor-mounted and wall-mounted configurations for maximum showroom space utilisation.",
        description:
          "Flip Page — sequential page-style browsing engineered for compact ceramic showrooms. Available floor and wall mounted.",
        thumbnail: IMAGES.blackFramework,
        panel_capacity: "Up to 40 panels · floor or wall mounted",
        material: "NIZONE CRC steel — 2\"×1\" frame, 3\"×1.5\" or 4\"×4\" main",
        finish: "Premium powder coating (Matte Black / Pure White / Industrial Grey)",
        tile_sizes: TILE_SIZES,
        configurations: CONFIGS,
        colours: COLOURS,
        specifications: { ...BASE_SPECS, Mounting: "Floor mounted or wall mounted", Geometry: "Linear Page Layout", Orientation: "Continuous page sequence" },
        standard_features: COMMON_FEATURES,
        highlights: HIGHLIGHTS,
        key_features: KEY_FEATURES,
        applications: APPLICATIONS,
        best_for: [
          "Compact Ceramic Showrooms",
          "Wall Mounted Installations",
          "Space Saving Layouts",
          "Sequential Product Display",
        ],
        why_choose: [
          "Continuous page-style browsing",
          "Wall-mounted option available",
          "Maximum space utilization",
          "Clean organized presentation",
          "Ideal for compact showrooms",
        ],
        features: [
          "2\" × 1\" NIZONE CRC panel frame",
          "3\" × 1.5\" or 4\" × 4\" NIZONE CRC main structure",
          "Precision top & bottom bush rotation",
          "Heavy-duty rubber buffer between every panel",
          "18 mm premium plywood backing",
          "Floor and wall mounted configurations",
        ],
        seo_title: "STENZIA Flip Page — Page Style Booklet Tile Display | Stenzia Displays",
        seo_description: "Flip Page booklet tile display — NIZONE CRC construction, precision bush rotation, rubber buffer protection and 18 mm premium ply. Floor or wall mounted.",
        og_title: "STENZIA Flip Page — Page Style Booklet Display",
        og_description: "Page-style booklet tile display engineered with NIZONE CRC construction, precision bush rotation and 18 mm premium plywood.",
        meta_keywords: "Flip Page, page style booklet, sequential tile display, wall mounted tile booklet, NIZONE CRC booklet, Stenzia Flip",
        featured: true,
      }),
      make({
        slug: "flip-bombay",
        name: "STENZIA Flip Bombay",
        technical_name: "Bombay Style Booklet Tile Display System",
        series: SERIES,
        model_designation: "Bombay Style",
        category: "booklet-display-systems",
        short: "Dual Sections. Faster Product Browsing.",
        overview:
          "The Flip Bombay system divides display panels into two completely independent groups positioned on the left and right sides — for example, 12 + 12 or 20 + 20 panels, depending on the chosen panel count.",
        engineering_description:
          "Customers can browse one panel group without moving the opposite side, improving accessibility and product organization.",
        construction:
          "Independent left and right panel groups improve browsing efficiency while handling larger product collections.",
        description:
          "Flip Bombay — dual independent left/right panel groups engineered for large ceramic showrooms and high-traffic dealers.",
        thumbnail: IMAGES.tileTexture,
        panel_capacity: "Up to 40 panels · dual section (left + right)",
        material: "NIZONE CRC steel — 2\"×1\" frame, 3\"×1.5\" or 4\"×4\" main",
        finish: "Premium powder coating (Matte Black / Pure White / Industrial Grey)",
        tile_sizes: TILE_SIZES,
        configurations: CONFIGS,
        colours: COLOURS,
        specifications: { ...BASE_SPECS, Section_Layout: "Independent left & right panel groups (configuration depends on total panel count — e.g. 12 + 12, 20 + 20)", Geometry: "Dual Wing Layout", Orientation: "Dual section booklet" },
        standard_features: COMMON_FEATURES,
        highlights: HIGHLIGHTS,
        key_features: KEY_FEATURES,
        applications: APPLICATIONS,
        best_for: [
          "Large Ceramic Showrooms",
          "Dealers",
          "High Traffic Showrooms",
          "Category Wise Product Display",
        ],
        why_choose: [
          "Independent left and right panel groups",
          "Faster browsing",
          "Large display capacity",
          "Better product organization",
          "Comfortable customer viewing",
        ],
        features: [
          "2\" × 1\" NIZONE CRC panel frame",
          "3\" × 1.5\" or 4\" × 4\" NIZONE CRC main structure",
          "Independent left & right panel groups",
          "Precision top & bottom bush rotation",
          "Heavy-duty rubber buffer between every panel",
          "18 mm premium plywood backing",
        ],
        seo_title: "STENZIA Flip Bombay — Bombay Style Booklet Tile Display | Stenzia Displays",
        seo_description: "Flip Bombay dual-section booklet tile display — independent left and right panel groups, NIZONE CRC construction and precision bush rotation.",
        og_title: "STENZIA Flip Bombay — Bombay Style Booklet Display",
        og_description: "Bombay-style booklet tile display engineered with independent left & right panel groups for large ceramic showrooms.",
        meta_keywords: "Flip Bombay, Bombay style booklet, dual section tile display, large tile booklet, NIZONE CRC booklet, Stenzia Flip",
        featured: true,
      }),
      make({
        slug: "flip-v",
        name: "STENZIA Flip V",
        technical_name: "V Style Booklet Tile Display System",
        series: SERIES,
        model_designation: "V Style",
        category: "booklet-display-systems",
        short: "Premium Presentation. Compact Footprint.",
        overview:
          "The Flip V system arranges display panels in a V-shaped configuration, allowing dual-side browsing while maintaining a compact footprint.",
        engineering_description:
          "Every panel rotates independently using precision bush assemblies while heavy-duty rubber buffers prevent direct panel contact.",
        construction:
          "Dual-side viewing with efficient showroom space utilisation.",
        description:
          "Flip V — V-shaped dual-side booklet tile display engineered for premium ceramic showrooms and architect display zones.",
        thumbnail: IMAGES.architecture,
        panel_capacity: "Up to 40 panels · V-shaped dual-side",
        material: "NIZONE CRC steel — 2\"×1\" frame, 3\"×1.5\" or 4\"×4\" main",
        finish: "Premium powder coating (Matte Black / Pure White / Industrial Grey)",
        tile_sizes: TILE_SIZES,
        configurations: CONFIGS,
        colours: COLOURS,
        specifications: { ...BASE_SPECS, Geometry: "V Shape Layout", Orientation: "Dual-side compact" },
        standard_features: COMMON_FEATURES,
        highlights: HIGHLIGHTS,
        key_features: KEY_FEATURES,
        applications: APPLICATIONS,
        best_for: [
          "Premium Ceramic Showrooms",
          "Dual Side Viewing",
          "Compact Layouts",
          "Architect Display Zones",
        ],
        why_choose: [
          "Dual-side viewing",
          "Compact V-shaped design",
          "Premium presentation",
          "Better accessibility",
          "Efficient showroom layout",
        ],
        features: [
          "2\" × 1\" NIZONE CRC panel frame",
          "3\" × 1.5\" or 4\" × 4\" NIZONE CRC main structure",
          "V-shaped dual-side panel geometry",
          "Precision top & bottom bush rotation",
          "Heavy-duty rubber buffer between every panel",
          "18 mm premium plywood backing",
        ],
        seo_title: "STENZIA Flip V — V Style Booklet Tile Display | Stenzia Displays",
        seo_description: "Flip V V-shaped dual-side booklet tile display — NIZONE CRC construction, precision bush rotation and 18 mm premium plywood. Compact premium footprint.",
        og_title: "STENZIA Flip V — V Style Booklet Display",
        og_description: "V-shaped dual-side booklet tile display engineered for premium ceramic showrooms and architect display zones.",
        meta_keywords: "Flip V, V style booklet, V shaped tile display, dual side booklet, premium tile booklet, NIZONE CRC booklet, Stenzia Flip",
        featured: true,
      }),
    ];
  })(),

  // ============================================================================
  // MOTION DISPLAY SYSTEMS — STENZIA Motion Series
  // ============================================================================
  ...(() => {
    const SERIES = "STENZIA Motion Series";
    const APPLICATIONS = [
      "Tile showrooms",
      "Ceramic display centres",
      "Compact retail layouts",
      "Architect studios",
      "Export display systems",
      "Premium ceramic galleries",
    ];
    const STANDARD = [
      "Premium STENZIA Sliding Mechanism",
      "Heavy-Duty NIZONE CRC Construction",
      "SS 202 Sliding Rail & Wheels",
      "Premium Nylon Guide Wheels",
      "Smooth Silent Operation",
      "Premium Powder Coating",
      "Custom Branding Available",
      "Made in India",
    ];
    const HIGHLIGHTS = [
      { icon: "Shield", title: "CRC Steel", body: "Heavy-duty NIZONE cold-rolled steel construction across the frame and main structure." },
      { icon: "MoveHorizontal", title: "SS202 Rail", body: "Stainless-steel SS 202 sliding rail and panel wheels — silent, friction-free travel." },
      { icon: "Cpu", title: "Premium Nylon Wheels", body: "Top nylon guide wheels — long service life, low maintenance." },
      { icon: "Sparkles", title: "Powder Coated", body: "Electrostatic powder coating — scratch, UV and humidity resistant." },
    ];
    const KEY_FEATURES = [
      { icon: "Shield", title: "Heavy-Duty CRC Construction", body: "Premium NIZONE cold-rolled steel pipe with reinforced welded joints — engineered for continuous showroom usage." },
      { icon: "MoveHorizontal", title: "SS 202 Sliding Rail", body: "Stainless-steel SS 202 rail and SS 202 panel wheels deliver silent, friction-free panel travel." },
      { icon: "Cpu", title: "Premium Nylon Guide Wheels", body: "Top nylon guide wheels provide smooth, low-maintenance operation over the full service life." },
      { icon: "Sparkles", title: "Premium Powder Coating", body: "Electrostatic powder coating in any RAL colour — scratch, UV and humidity resistant." },
      { icon: "Award", title: "Architect-Grade Engineering", body: "CAD-driven design, FEA-verified load capacity and tight dimensional tolerances." },
      { icon: "Ship", title: "Made in India", body: "Container-optimised knock-down construction. Designed and manufactured by STENZIA Displays." },
    ];
    const SIZES = ["6' × 4'", "8' × 4'", "10' × 4'", "Custom"];
    const CONFIGS = ["24 Panels", "36 Panels", "40 Panels", "45 Panels"];
    const COLOURS = ["Matte Black", "Pure White", "Industrial Grey"];
    const BASE_SPECS = {
      Sliding_Rail: "SS 202 sliding rail",
      Sliding_Wheels: "SS 202 panel wheels",
      Top_Guide_Wheels: "Premium nylon guide wheels",
      Finish: "Premium electrostatic powder coating",
      Colours: "Matte Black · Pure White · Industrial Grey",
      Available_Sizes: "6'×4', 8'×4', 10'×4' (custom on request)",
      Configurations: "24 / 36 / 40 / 45 panels (max 45)",
      Construction: "Heavy duty industrial — CAD designed, jig-welded, 27-point QC",
      Origin: "Designed & manufactured by STENZIA Displays · Made in India",
    };

    return [
      make({
        slug: "motion-lite",
        name: "STENZIA Motion Lite",
        technical_name: "Without-Plywood Tile Slider Display System",
        series: SERIES,
        model_designation: "Lite",
        category: "motion-display-systems",
        short: "Plywood-Free Construction. Lower Weight. Same Premium Slide.",
        overview:
          "Motion Lite is a lightweight vertical tile slider that fixes tiles directly to the panel frame using side clips — eliminating the plywood backing entirely. The result is a lower-weight, lower-cost, easier-to-ship slider that still uses the same premium STENZIA sliding mechanism as the Glide Series.",
        engineering_description:
          "1.5\" × 1.5\" NIZONE CRC panel frames run inside a 3\" × 1.5\" CRC main structure on SS 202 sliding rails. Premium nylon top guide wheels keep travel silent and parallel. Tiles are fixed using a side clip system on both faces — no plywood, no adhesives.",
        construction:
          "All-steel welded chassis, side-clip tile fixing, dual-face presentation, knock-down construction for container shipping.",
        description:
          "Motion Lite — lightweight plywood-free tile slider with side-clip fixing and the same premium STENZIA sliding mechanism.",
        thumbnail: IMAGES.architecture,
        panel_capacity: "Dual-side · up to 45 panels",
        material: "NIZONE CRC pipe — 1.5\"×1.5\" frame, 3\"×1.5\" main",
        finish: "Premium powder coating (any RAL colour)",
        tile_sizes: SIZES,
        configurations: CONFIGS,
        colours: COLOURS,
        specifications: {
          Panel_Frame: "1.5\" × 1.5\" NIZONE CRC pipe",
          Main_Structure: "3\" × 1.5\" NIZONE CRC steel",
          Panel: "No plywood — direct tile fixing",
          Tile_Fixing: "Side clip fixing system (both faces)",
          ...BASE_SPECS,
        },
        standard_features: STANDARD,
        highlights: HIGHLIGHTS,
        key_features: KEY_FEATURES,
        applications: APPLICATIONS,
        best_for: [
          "Lightweight Installations",
          "Export & Shipping Cost Optimisation",
          "Humid & Coastal Showrooms",
          "Dual-Side Tile Display",
        ],
        why_choose: [
          "No plywood required",
          "Lower weight per unit",
          "Lower manufacturing cost",
          "Easier transportation and handling",
          "Tiles fixed directly using side clips",
          "Both sides display tiles",
          "Same premium sliding mechanism as Glide Series",
        ],
        features: [
          "1.5\" × 1.5\" NIZONE CRC panel frame",
          "3\" × 1.5\" NIZONE CRC main structure",
          "SS 202 sliding rail + SS 202 panel wheels",
          "Premium nylon top guide wheels",
          "Side-clip direct tile fixing — no plywood",
          "Dual-face tile presentation",
        ],
        seo_title: "STENZIA Motion Lite — Without-Plywood Tile Slider | Stenzia Displays",
        seo_description: "Motion Lite lightweight plywood-free tile slider — NIZONE CRC construction, SS 202 sliding hardware and side-clip tile fixing on both faces.",
        og_title: "STENZIA Motion Lite — Without-Plywood Tile Slider",
        og_description: "Lightweight plywood-free tile slider with direct side-clip tile fixing and the premium STENZIA sliding mechanism.",
        meta_keywords: "Motion Lite, without plywood slider, lightweight tile slider, side clip tile fixing, NIZONE CRC, Stenzia Motion",
        featured: true,
      }),
      make({
        slug: "motion-flex",
        name: "STENZIA Motion Flex",
        technical_name: "Flexible Tile Slider Display System",
        series: SERIES,
        model_designation: "Flex",
        category: "motion-display-systems",
        short: "Adjustable Width. One Frame. Multiple Panel Sizes.",
        overview:
          "Motion Flex is an adjustable-width tile slider engineered around flexible sheet-metal channels. The same chassis accommodates multiple panel widths — for example a 4 ft frame can be reconfigured to 64\" or 6 ft — without changing the main structure.",
        engineering_description:
          "Panel frames are sized to the panel specification (2\"×1\", 1.5\"×1.5\" or 2\"×2\" NIZONE CRC) and ride inside a 3\"×1.5\" CRC main structure on SS 202 sliding rails. Premium nylon top guide wheels keep the system silent. Flexible sheet-metal channels reconfigure the panel width in the field.",
        construction:
          "Welded CRC chassis with flexible sheet-metal width channels, side-clip tile fixing and the same premium STENZIA sliding mechanism as the Glide Series.",
        description:
          "Motion Flex — adjustable-width tile slider with flexible sheet-metal channels and the premium STENZIA sliding mechanism.",
        thumbnail: IMAGES.tileTexture,
        panel_capacity: "Adjustable-width · up to 45 panels",
        material: "NIZONE CRC pipe — 2\"×1\", 1.5\"×1.5\" or 2\"×2\" frame; 3\"×1.5\" main",
        finish: "Premium powder coating (any RAL colour)",
        tile_sizes: SIZES,
        configurations: CONFIGS,
        colours: COLOURS,
        specifications: {
          Panel_Frame: "2\"×1\", 1.5\"×1.5\" or 2\"×2\" NIZONE CRC pipe (per panel specification)",
          Main_Structure: "3\" × 1.5\" NIZONE CRC steel",
          Width_Adjustment: "Flexible sheet-metal channel system",
          Panel: "No plywood — direct tile fixing",
          ...BASE_SPECS,
        },
        standard_features: STANDARD,
        highlights: HIGHLIGHTS,
        key_features: KEY_FEATURES,
        applications: APPLICATIONS,
        best_for: [
          "Showrooms Running Multiple Tile Sizes",
          "Future-Proof Display Investments",
          "Dealer & Distributor Display Centres",
          "Re-configurable Retail Layouts",
        ],
        why_choose: [
          "Adjustable panel width on one chassis",
          "Re-configurable in the field",
          "Same premium STENZIA sliding mechanism",
          "Lower long-term tooling cost",
          "Supports multiple panel sizes per unit",
        ],
        features: [
          "Flexible sheet-metal width channels",
          "2\"×1\", 1.5\"×1.5\" or 2\"×2\" NIZONE CRC panel frame",
          "3\" × 1.5\" NIZONE CRC main structure",
          "SS 202 sliding rail + SS 202 panel wheels",
          "Premium nylon top guide wheels",
          "Up to 45 panels per unit",
        ],
        seo_title: "STENZIA Motion Flex — Flexible Tile Slider | Stenzia Displays",
        seo_description: "Motion Flex adjustable-width tile slider — flexible sheet-metal channels, NIZONE CRC construction and the premium STENZIA sliding mechanism.",
        og_title: "STENZIA Motion Flex — Flexible Tile Slider",
        og_description: "Adjustable-width tile slider engineered with flexible sheet-metal channels and the premium STENZIA sliding mechanism.",
        meta_keywords: "Motion Flex, flexible tile slider, adjustable width slider, reconfigurable tile rack, NIZONE CRC, Stenzia Motion",
        featured: true,
      }),
      make({
        slug: "motion-push",
        name: "STENZIA Motion Push",
        technical_name: "Push & Pull Tile Display System",
        series: SERIES,
        model_designation: "Push & Pull",
        category: "motion-display-systems",
        short: "Maximum Display. Minimum Space.",
        overview:
          "Motion Push is engineered for showrooms that need maximum SKU density in the smallest possible floor area. Every panel can be pulled completely outside for a full unobstructed view, then pushed back into position — ideal for compact ceramic showrooms.",
        engineering_description:
          "2\"×1\" NIZONE CRC panel frames carry 18 mm premium plywood and ride inside a 3\"×1.5\" CRC main structure on SS 202 sliding rails. Premium nylon top guide wheels keep the push-and-pull travel silent and parallel. Both faces display tiles.",
        construction:
          "Welded CRC chassis, push-and-pull panel travel, dual-face 18 mm premium plywood panels, knock-down construction for container shipping.",
        description:
          "Motion Push — push-and-pull tile display engineered for maximum SKU density in compact ceramic showrooms.",
        thumbnail: IMAGES.steelRack,
        panel_capacity: "Dual-side · up to 45 panels (push & pull)",
        material: "NIZONE CRC pipe — 2\"×1\" frame, 3\"×1.5\" main",
        finish: "Premium powder coating (any RAL colour)",
        tile_sizes: SIZES,
        configurations: CONFIGS,
        colours: COLOURS,
        specifications: {
          Panel_Frame: "2\" × 1\" NIZONE CRC pipe",
          Main_Structure: "3\" × 1.5\" NIZONE CRC steel",
          Panel: "18 mm premium plywood — surfaced with premium grey grooved carpet bonded using industrial-grade adhesive",
          Mechanism: "Push & pull — full pull-out panel travel",
          Display: "Dual-face tile presentation",
          ...BASE_SPECS,
        },
        standard_features: STANDARD,
        highlights: HIGHLIGHTS,
        key_features: KEY_FEATURES,
        applications: APPLICATIONS,
        best_for: [
          "Compact Ceramic Showrooms",
          "High-Density SKU Display",
          "Dual-Side Browsing",
          "Premium Retail Display",
        ],
        why_choose: [
          "Every panel pulls completely outside for viewing",
          "Maximum display inside minimum space",
          "Both sides display tiles",
          "Same premium STENZIA sliding mechanism",
          "Ideal for compact showrooms",
        ],
        features: [
          "2\" × 1\" NIZONE CRC panel frame",
          "3\" × 1.5\" NIZONE CRC main structure",
          "18 mm premium plywood panel backing",
          "SS 202 sliding rail + SS 202 panel wheels",
          "Premium nylon top guide wheels",
          "Full pull-out + push-back panel travel",
        ],
        seo_title: "STENZIA Motion Push — Push & Pull Tile Display | Stenzia Displays",
        seo_description: "Motion Push push-and-pull tile display — NIZONE CRC construction, 18 mm premium plywood and the premium STENZIA sliding mechanism. Maximum SKU density.",
        og_title: "STENZIA Motion Push — Push & Pull Tile Display",
        og_description: "Push-and-pull tile display engineered for maximum SKU density in compact ceramic showrooms.",
        meta_keywords: "Motion Push, push pull tile display, pull out tile slider, compact tile display, NIZONE CRC, Stenzia Motion",
        featured: true,
      }),
    ];
  })(),

  // ============================================================================
  // 360 DISPLAY SYSTEMS — STENZIA 360 Series
  // ============================================================================
  ...(() => {
    const SERIES = "STENZIA 360 Series";
    const APPLICATIONS = [
      "Tile sample display",
      "Showroom counters",
      "Compact display zones",
      "Architect studios",
      "Premium ceramic showrooms",
      "Export display centres",
    ];
    const COLOURS = ["Matte Black", "Pure White", "Industrial Grey"];
    const HIGHLIGHTS = [
      { icon: "Shield", title: "CRC Construction", body: "Heavy-duty NIZONE cold-rolled steel pipe — engineered for continuous showroom usage." },
      { icon: "MoveHorizontal", title: "360 Degree Rotation", body: "Smooth 360-degree panel rotation engineered into every unit." },
      { icon: "Cpu", title: "Heavy Duty Nylon Wheels", body: "Heavy-duty nylon bottom wheels and top guide wheels — silent, long service life." },
      { icon: "Sparkles", title: "Powder Coated", body: "Premium electrostatic powder coating — scratch, UV and humidity resistant." },
    ];
    const KEY_FEATURES = [
      { icon: "Shield", title: "Heavy Duty CRC Construction", body: "Heavy-duty NIZONE cold-rolled steel pipe with reinforced welded joints — built for premium showroom usage." },
      { icon: "MoveHorizontal", title: "360 Degree Rotation", body: "Smooth 360-degree panel rotation enables full sample browsing from one fixed position." },
      { icon: "Cpu", title: "Heavy Duty Nylon Wheels", body: "Heavy-duty nylon bottom and top guide wheels deliver silent, low-friction movement." },
      { icon: "Settings2", title: "Push & Pull Mechanism", body: "Push-and-pull panel travel pulls each panel completely outside for unobstructed viewing." },
      { icon: "Sparkles", title: "Premium Powder Coating", body: "Premium electrostatic powder coating in matte black, pure white or industrial grey." },
      { icon: "Ship", title: "Made in India", body: "Container-optimised knock-down construction. Designed and manufactured by STENZIA Displays." },
    ];

    return [
      make({
        slug: "360-compact",
        name: "STENZIA 360 Compact",
        technical_name: "360 Degree Rotating Compact Tile Display System",
        series: SERIES,
        model_designation: "Compact",
        category: "rotating-display-systems",
        short: "Compact 360 rotation. Maximum sample density. Minimum footprint.",
        overview:
          "STENZIA 360 Compact is a compact rotating display stand designed for tile sample presentation within the minimum possible floor space. Five rotating panels are mounted around a central structure with tiles displayed on both faces — customers rotate the display to compare samples instantly.",
        engineering_description:
          "Five 2 ft × 4 ft panels are arranged around a 2\" × 2\" NIZONE CRC central structure. Each panel uses an 18 × 18 mm CRC steel pipe frame and displays tiles on both faces — delivering 10 sample positions per unit in a minimal counter footprint.",
        construction:
          "Welded CRC chassis with central rotating column, dual-face panel mounts and adjustable feet. Knock-down construction for container shipping.",
        description:
          "STENZIA 360 Compact — five-panel compact rotating display for tile samples with dual-face presentation and a minimal footprint.",
        thumbnail: IMAGES.heroAlt,
        panel_capacity: "5 panels · dual-side · 10 sample positions",
        material: "NIZONE CRC steel pipe — 18×18 mm frame, 2\"×2\" central structure",
        finish: "Premium powder coating (Matte Black / Pure White / Industrial Grey)",
        tile_sizes: ["2 ft × 4 ft", "Custom"],
        configurations: ["5 Panels (standard)", "Custom"],
        colours: COLOURS,
        specifications: {
          Panel_Size: "2 ft × 4 ft",
          Panels: "5",
          Display: "Dual side",
          Total_Display: "10 tile samples",
          Panel_Frame: "18 × 18 mm CRC steel pipe",
          Main_Structure: "2\" × 2\" CRC steel pipe",
          Rotation: "360 degree central rotation",
          Finish: "Premium electrostatic powder coating",
          Colours: "Matte Black · Pure White · Industrial Grey",
          Construction: "Heavy duty industrial — CAD designed, jig-welded, 27-point QC",
          Origin: "Designed & manufactured by STENZIA Displays · Made in India",
        },
        standard_features: [
          "Heavy Duty CRC Construction",
          "360 Degree Central Rotation",
          "Dual Side Tile Display",
          "Compact Counter Footprint",
          "Premium Powder Coating",
          "Custom Branding Available",
          "Made in India",
        ],
        highlights: HIGHLIGHTS,
        key_features: KEY_FEATURES,
        applications: APPLICATIONS,
        best_for: [
          "Tile sample display",
          "Showroom counters",
          "Compact display zones",
          "Architect studios",
        ],
        why_choose: [
          "Minimal footprint",
          "360 degree rotation",
          "Dual side display",
          "Compact showroom solution",
          "Easy sample comparison",
        ],
        features: [
          "Five rotating panels around a central column",
          "18 × 18 mm CRC steel panel frame",
          "2\" × 2\" CRC central structure",
          "Dual-face tile presentation",
          "10 sample positions per unit",
          "Custom panel sizes on request",
        ],
        seo_title: "STENZIA 360 Compact — 360 Degree Compact Tile Display | Stenzia Displays",
        seo_description: "STENZIA 360 Compact — five-panel 360 degree rotating tile sample display. Dual-face presentation, CRC construction and minimal counter footprint.",
        og_title: "STENZIA 360 Compact — 360 Degree Compact Tile Display",
        og_description: "Compact 360 degree rotating tile sample display engineered for minimal showroom counter footprint.",
        meta_keywords: "STENZIA 360 Compact, 360 rotating tile display, compact tile sample display, counter tile display, dual side rotating display",
        featured: true,
      }),
      make({
        slug: "360-motion",
        name: "STENZIA 360 Motion",
        technical_name: "Push & Pull 360 Degree Tile Display System",
        series: SERIES,
        model_designation: "Motion",
        category: "rotating-display-systems",
        short: "Push & Pull. 360 Degree Rotation. Maximum Display Capacity.",
        overview:
          "STENZIA 360 Motion combines push-and-pull panel travel with independent 360-degree panel rotation. Every panel can be pulled outward from the chassis, rotated for unobstructed viewing, then pushed back into position — an advanced display system engineered for premium ceramic showrooms.",
        engineering_description:
          "2\" × 1\" NIZONE CRC panel frames carry 18 mm premium plywood and ride directly on the floor on heavy-duty nylon bottom wheels — no SS 202 rail, no SS 202 wheels. Heavy-duty nylon guide wheels stabilise the top. Each panel rotates independently through 360 degrees and displays tiles on both faces.",
        construction:
          "Welded CRC chassis with floor-running heavy-duty nylon wheels, independent 360-degree panel rotation and dual-face 18 mm premium plywood panels. Knock-down construction for container shipping.",
        description:
          "STENZIA 360 Motion — push-and-pull 360 degree rotating tile display with heavy-duty nylon wheels and dual-face plywood panels.",
        thumbnail: IMAGES.steelRack,
        panel_capacity: "Push & pull · dual-side · 360 rotation per panel",
        material: "NIZONE CRC steel — 2\"×1\" frame, 3\"×1.5\" main",
        finish: "Premium powder coating (Matte Black / Pure White / Industrial Grey)",
        tile_sizes: ["6' × 4'", "8' × 4'", "10' × 4'", "Custom"],
        configurations: ["Custom panel count"],
        colours: COLOURS,
        specifications: {
          Panel: "18 mm premium plywood — surfaced with premium grey grooved carpet bonded using industrial-grade adhesive",
          Display: "Dual side",
          Panel_Frame: "2\" × 1\" CRC steel pipe",
          Main_Structure: "3\" × 1.5\" CRC steel pipe",
          Bottom_Wheels: "Heavy duty nylon wheels (floor-running)",
          Top_Wheels: "Heavy duty nylon guide wheels",
          Movement: "Push & pull",
          Rotation: "360 degree per panel",
          Sliding: "Direct floor movement — no SS rail, no SS 202 wheels",
          Finish: "Premium electrostatic powder coating",
          Colours: "Matte Black · Pure White · Industrial Grey",
          Construction: "Heavy duty industrial — CAD designed, jig-welded, 27-point QC",
          Origin: "Designed & manufactured by STENZIA Displays · Made in India",
        },
        standard_features: [
          "Heavy Duty CRC Construction",
          "360 Degree Panel Rotation",
          "Push & Pull Mechanism",
          "Heavy Duty Nylon Wheels",
          "No Floor Rail Required",
          "Dual Side Tile Display",
          "Premium Powder Coating",
          "Custom Branding Available",
          "Made in India",
        ],
        highlights: HIGHLIGHTS,
        key_features: KEY_FEATURES,
        applications: [
          "Premium ceramic showrooms",
          "High product density retail",
          "Compact showroom layouts",
          "Large tile collection display",
          "Architect specification rooms",
          "Export display centres",
        ],
        best_for: [
          "Premium ceramic showrooms",
          "High product density",
          "Compact layouts",
          "Large tile collections",
        ],
        why_choose: [
          "Push & pull movement",
          "360 degree rotation per panel",
          "Maximum display capacity",
          "No floor rail required",
          "Heavy duty nylon wheel system",
          "Dual side display",
        ],
        features: [
          "2\" × 1\" NIZONE CRC panel frame",
          "3\" × 1.5\" NIZONE CRC main structure",
          "18 mm premium plywood panel backing",
          "Heavy-duty nylon bottom wheels (direct floor)",
          "Heavy-duty nylon top guide wheels",
          "Independent 360 degree rotation per panel",
        ],
        seo_title: "STENZIA 360 Motion — Push & Pull 360 Degree Tile Display | Stenzia Displays",
        seo_description: "STENZIA 360 Motion — push-and-pull 360 degree rotating tile display with heavy-duty nylon wheels, dual-face plywood panels and CRC construction.",
        og_title: "STENZIA 360 Motion — Push & Pull 360 Degree Tile Display",
        og_description: "Push-and-pull tile display with independent 360 degree panel rotation engineered for premium ceramic showrooms.",
        meta_keywords: "STENZIA 360 Motion, push pull 360 display, rotating tile slider, 360 tile rack, nylon wheel tile display, dual side rotating display",
        featured: true,
      }),
    ];
  })(),

  // ============================================================================
  // MOCKUP DISPLAY SYSTEMS — STENZIA Mockup Series
  // ============================================================================
  ...(() => {
    const SERIES = "STENZIA Mockup Series";
    const APPLICATIONS = [
      "Ceramic showrooms",
      "Architect studios",
      "Interior designers",
      "Experience centres",
      "Retail display",
      "Export projects",
    ];
    const COLOURS = ["Matte Black", "Pure White", "Industrial Grey"];
    const SIZES = ["4 ft", "6 ft", "8 ft", "10 ft", "12 ft", "Custom"];
    const STANDARD = [
      "Heavy Duty CRC Structure",
      "Premium Plywood (12 / 18 mm)",
      "Premium Grey Grooved Carpet",
      "Industrial-Grade Adhesive Bonding",
      "Modular Construction",
      "Custom Sizes",
      "Easy Installation",
      "Premium Powder Coating",
      "Made in India",
    ];
    const HIGHLIGHTS = [
      { icon: "Shield", title: "CRC Structure", body: "2\" × 2\" NIZONE CRC steel pipe — engineered for long-term showroom installation." },
      { icon: "Cpu", title: "Premium Plywood", body: "12 mm / 18 mm premium plywood substrate with industrial-grade bonded carpet surface." },
      { icon: "Sparkles", title: "Grey Grooved Carpet", body: "Premium grey grooved carpet professionally bonded using industrial-grade adhesive." },
      { icon: "MoveHorizontal", title: "Modular Build", body: "Knock-down modular construction — easy on-site installation and reconfiguration." },
    ];
    const KEY_FEATURES = [
      { icon: "Shield", title: "Heavy Duty CRC Structure", body: "2\" × 2\" NIZONE cold-rolled steel pipe with reinforced welded joints — engineered for continuous showroom installation." },
      { icon: "Cpu", title: "Premium Plywood", body: "12 mm / 18 mm premium plywood substrate engineered to carry tile load without warping." },
      { icon: "Sparkles", title: "Premium Grey Grooved Carpet", body: "Premium grey grooved carpet professionally bonded to the plywood substrate using industrial-grade adhesive." },
      { icon: "Settings2", title: "Modular Construction", body: "Knock-down modular construction enables easy on-site installation and future reconfiguration." },
      { icon: "MoveHorizontal", title: "Custom Sizes", body: "Available in 4 / 6 / 8 / 10 / 12 ft or custom sizes engineered around your exact showroom footprint." },
      { icon: "Ship", title: "Made in India", body: "Container-optimised knock-down construction. Designed and manufactured by STENZIA Displays." },
    ];
    const BASE_SPECS = {
      Structure: "2\" × 2\" NIZONE CRC steel pipe",
      Plywood: "12 mm / 18 mm premium plywood",
      Surface: "Premium grey grooved carpet bonded using industrial-grade adhesive",
      Adhesive: "Industrial-grade adhesive",
      Construction: "Knock-down modular — CAD designed, jig-welded, 27-point QC",
      Finish: "Premium electrostatic powder coating",
      Colours: "Matte Black · Pure White · Industrial Grey",
      Available_Sizes: "4 / 6 / 8 / 10 / 12 ft (custom on request)",
      Origin: "Designed & manufactured by STENZIA Displays · Made in India",
    };

    const mk = ({ slug, name, model_designation, room, focus }) =>
      make({
        slug,
        name: `STENZIA ${name}`,
        technical_name: `${name} Mockup Display System`,
        series: SERIES,
        model_designation,
        category: "mockup-display-systems",
        short: `Realistic ${room.toLowerCase()} ceramic tile presentation for premium showrooms.`,
        overview: `STENZIA ${name} is a premium mockup display engineered to recreate a realistic ${room.toLowerCase()} environment inside ceramic showrooms, experience centres and architectural display spaces. ${focus}`,
        engineering_description: `2\" × 2\" NIZONE CRC steel structure carries 12 mm / 18 mm premium plywood panels surfaced with premium grey grooved carpet, professionally bonded using industrial-grade adhesive. Knock-down modular construction simplifies on-site installation.`,
        construction: `Welded CRC chassis, modular plywood panels with bonded grey grooved carpet surface, premium powder-coated finish. Knock-down design for shipping and on-site installation.`,
        description: `STENZIA ${name} — premium mockup display engineered for realistic ${room.toLowerCase()} tile presentation in showrooms and experience centres.`,
        thumbnail: IMAGES.heroShowroom,
        panel_capacity: `Custom mockup · ${room} environment`,
        material: '2" × 2" NIZONE CRC + 12 mm / 18 mm premium plywood + grey grooved carpet',
        finish: "Premium powder coating (Matte Black / Pure White / Industrial Grey)",
        tile_sizes: SIZES,
        configurations: ["Custom mockup layout"],
        colours: COLOURS,
        specifications: BASE_SPECS,
        standard_features: STANDARD,
        highlights: HIGHLIGHTS,
        key_features: KEY_FEATURES,
        applications: APPLICATIONS,
        best_for: [
          `${room} tile presentation`,
          "Architect studios",
          "Experience centres",
          "Retail display",
        ],
        why_choose: [
          "Heavy Duty CRC Structure",
          "Premium Plywood (12 / 18 mm)",
          "Premium Grey Grooved Carpet",
          "Industrial-Grade Adhesive Bonding",
          "Modular Knock-Down Construction",
          "Custom Sizes (4–12 ft)",
        ],
        features: [
          '2" × 2" NIZONE CRC steel structure',
          "12 mm / 18 mm premium plywood",
          "Premium grey grooved carpet surface",
          "Industrial-grade adhesive bonding",
          "Knock-down modular construction",
          "Custom 4–12 ft sizes",
        ],
        seo_title: `STENZIA ${name} — ${room} Mockup Display | Stenzia Displays`,
        seo_description: `STENZIA ${name} mockup display — CRC structure, premium plywood and bonded grey grooved carpet engineered for realistic ${room.toLowerCase()} tile presentation.`,
        og_title: `STENZIA ${name} — ${room} Mockup Display`,
        og_description: `Premium ${room.toLowerCase()} mockup display engineered for realistic tile presentation in showrooms and experience centres.`,
        meta_keywords: `STENZIA ${name}, ${room.toLowerCase()} mockup display, tile mockup, showroom mockup, ceramic mockup, ${name.toLowerCase()} mockup`,
        featured: true,
      });

    return [
      mk({ slug: "bathroom-mockup", name: "Bathroom Mockup", model_designation: "Bathroom", room: "Bathroom",
           focus: "Engineered around bathroom tile zones, sanitary fittings and wet-area floor presentation." }),
      mk({ slug: "bedroom-mockup", name: "Bedroom Mockup", model_designation: "Bedroom", room: "Bedroom",
           focus: "Engineered around bedroom flooring and feature-wall tile presentation in a residential context." }),
      mk({ slug: "kitchen-mockup", name: "Kitchen Mockup", model_designation: "Kitchen", room: "Kitchen",
           focus: "Engineered around kitchen splashback, countertop edge and floor tile presentation." }),
      mk({ slug: "living-room-mockup", name: "Living Room Mockup", model_designation: "Living Room", room: "Living Room",
           focus: "Engineered around living-room feature walls, floor tile layout and architectural detailing." }),
      mk({ slug: "commercial-mockup", name: "Commercial Mockup", model_designation: "Commercial", room: "Commercial",
           focus: "Engineered around commercial flooring, hospitality and high-traffic retail tile presentation." }),
      mk({ slug: "custom-mockup", name: "Custom Mockup", model_designation: "Custom", room: "Custom",
           focus: "Engineered around a bespoke brief — built to your exact room layout, dimensions and tile palette." }),
    ];
  })(),

];

// ---------- HELPERS ----------
const isActive = (p) => p.status !== "hidden";

export const getCategories = () => [...CATEGORIES].sort((a, b) => a.order - b.order);
export const getCategoryBySlug = (slug) => CATEGORIES.find((c) => c.slug === slug);
export const getActiveProducts = () => CATALOG.filter(isActive);
export const getFeaturedProducts = (limit) => {
  const arr = getActiveProducts().filter((p) => p.featured);
  return typeof limit === "number" ? arr.slice(0, limit) : arr;
};
export const getProductsByCategory = (categorySlug) =>
  getActiveProducts().filter((p) => p.category === categorySlug);
export const getProductBySlug = (slug) => CATALOG.find((p) => p.slug === slug && isActive(p));
export const getCategoryCounts = () =>
  Object.fromEntries(CATEGORIES.map((c) => [c.slug, getProductsByCategory(c.slug).length]));
